<template>
  <SeedJung_Header></SeedJung_Header>
  <div class="consultation-detail">
    <h2 class="h2_ing">상담 신청 상세 정보</h2>
    <div v-if="consultation" class="consultation-content">
      <h3>{{ consultation.title }}</h3>
      <p><strong>작성자:</strong> {{ consultation.author }}</p>
      <p><strong>작성일:</strong> {{ formatDate(consultation.createdAt) }}</p>
      <p><strong>상태:</strong> {{ consultation.status }}</p>
      <div class="content">
        <textarea 
          v-model="consultation.content" 
          @input="adjustTextareaHeight"
          ref="textarea"
          class="auto-resize-textarea"
        ></textarea>
      </div>
      <div class="button-container">
        <div class="left-buttons">
          <button class="list-button" @click="goToList">목록</button>
        </div>
        <div class="right-buttons">
          <!-- 관리자용 승인/취소 토글 버튼 -->
          <button 
            v-if="isAdmin" 
            :class="['toggle-button', consultation.status === '승인완료' ? 'cancel-status' : 'approve-status']"
            @click="toggleApprovalStatus"
          >
            {{ consultation.status === '승인완료' ? '상담승인 취소' : '상담승인' }}
          </button>
          <button class="save-button" @click="saveConsultation">저장</button>
          <button class="delete-button" @click="deleteConsultation">삭제</button>
        </div>
      </div>

      <!-- 댓글 섹션 -->
      <div class="comments-section">
        <h4>Comments</h4>
        <div v-if="comments.length === 0" class="no-comments">
          아직 댓글이 없습니다.
        </div>
        <div v-else class="comments-list">
          <div v-for="comment in comments" :key="comment.id" class="comment">
            <div v-if="editingCommentId === comment.id">
              <textarea v-model="editedCommentContent" class="edit-comment-textarea"></textarea>
              <div class="comment-edit-buttons">
                <button @click="saveEditedComment(comment.id)" class="save-edit-btn">저장</button>
                <button @click="cancelEditComment" class="cancel-edit-btn">취소</button>
              </div>
            </div>
            <div v-else>
              <p>
                <strong>{{ comment.author }}</strong>
                <span class="comment-date">({{ formatDate(comment.createdAt) }})</span>
                <span v-if="comment.isAdminComment" class="admin-badge">관리자</span>
              </p>
              <p v-html="comment.content"></p>
              <div class="comment-actions" v-if="comment.author === consultation.author || isAdmin">
                <button @click="editComment(comment)" class="edit-comment-btn">수정</button>
                <button @click="deleteComment(comment.id)" class="delete-comment-btn">삭제</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 댓글 작성 폼 -->
        <div class="comment-form">
          <textarea v-model="newComment" placeholder="댓글을 입력하세요" class="comment-textarea"></textarea>
          <button @click="submitComment" class="comment-submit-btn">댓글 작성</button>
        </div>
      </div>
    </div>
    <div v-else class="loading">
      로딩 중...
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SeedJung_Header from './SeedJung_Header.vue';

export default {
  name: 'SeedJung_ConsultationDetail',
  components: {
    SeedJung_Header
  },
  props: ['id'],
  data() {
    return {
      consultation: null,
      comments: [],
      newComment: '',
      editingCommentId: null,
      editedCommentContent: ''
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters.currentUser?.username?.trim() === 'alscofld95';
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    async fetchConsultationDetail() {
      try {
        const response = await axios.get(`/api/consultations/${this.id}`);
        this.consultation = response.data;
        this.$nextTick(() => {
          this.adjustTextareaHeight();
        });
      } catch (error) {
        console.error('상담 신청 상세 정보를 불러오는 데 실패했습니다:', error);
        alert('상담 신청 정보를 불러오는 데 실패했습니다. 다시 시도해 주세요.');
        window.location.href = '/consulting';
      }
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.textarea;
      if (textarea) {
        const scrollPos = window.pageYOffset || document.documentElement.scrollTop;
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
        window.scrollTo(0, scrollPos);
      }
    },
    async saveConsultation() {
      if (confirm('변경된 내용을 저장하시겠습니까?')) {
        try {
          const updatedConsultation = {
            id: this.consultation.id,
            title: this.consultation.title,
            author: this.consultation.author,
            content: this.consultation.content
          };
          const response = await axios.post('/api/consultations/update', updatedConsultation);
          console.log('상담 신청 내용 업데이트 완료:', response.data);
          alert('상담 신청 내용이 성공적으로 저장되었습니다.');
        } catch (error) {
          console.error('상담 신청 내용 업데이트 실패:', error);
          alert('상담 신청 내용 저장 중 오류가 발생했습니다. 다시 시도해 주세요.');
        }
      }
    },
    goToList() {
      this.$router.push('/consulting');
    },
    async deleteConsultation() {
      if (confirm('정말로 이 상담 신청을 삭제하시겠습니까? 이 작업은 취소할 수 없습니다.')) {
        try {
          await axios.delete(`/api/consultations/${this.consultation.id}`);
          alert('상담 신청이 성공적으로 삭제되었습니다.');
          this.goToList();
        } catch (error) {
          console.error('상담 신청 삭제 실패:', error);
          alert('상담 신청 삭제 중 오류가 발생했습니다. 다시 시도해 주세요.');
        }
      }
    },
    async toggleApprovalStatus() {
      const isApproving = this.consultation.status !== '승인완료';
      const confirmMessage = isApproving 
          ? '이 상담을 승인하시겠습니까?' 
          : '상담 승인을 취소하시겠습니까?';

      if (confirm(confirmMessage)) {
          try {
              const newStatus = isApproving ? '승인완료' : '대기중';
              const response = await axios.post(`/api/consultations/${this.consultation.id}/toggle-approval`, {
                  status: newStatus
              });
              
              // 상태와 제목 모두 업데이트
              this.consultation.status = newStatus;
              this.consultation.title = response.data.title;  // 서버에서 반환된 새 제목으로 업데이트
              
              if (isApproving) {
                  await this.addApprovalComment();
                  alert('상담이 승인되었습니다.');
              } else {
                  await this.addCancelComment();
                  alert('상담 승인이 취소되었습니다.');
              }
              
          } catch (error) {
              console.error('상담 상태 변경 실패:', error);
              alert('상담 상태 변경 중 오류가 발생했습니다. 다시 시도해 주세요.');
          }
      }
   },
    async addApprovalComment() {
      try {
        const approvalComment = "상담신청이 승인되었습니다.\n상담일정 조율을 위해 주기적인 댓글 확인 부탁드립니다.\n필요 시, 기재된 연락처로 연락 드립니다.";
        
        await axios.post(`/api/consultations/${this.id}/comments`, {
          content: approvalComment,
          author: '관리자',
          isAdminComment: true
        });
        
        await this.fetchComments();
      } catch (error) {
        console.error('승인 댓글 작성 실패:', error);
      }
    },
    async addCancelComment() {
      try {
        const cancelComment = "상담 승인이 취소되었습니다.";
        
        await axios.post(`/api/consultations/${this.id}/comments`, {
          content: cancelComment,
          author: '관리자',
          isAdminComment: true
        });
        
        await this.fetchComments();
      } catch (error) {
        console.error('취소 댓글 작성 실패:', error);
      }
    },
    async fetchComments() {
      try {
        const response = await axios.get(`/api/consultations/${this.id}/comments`);
        this.comments = response.data;
      } catch (error) {
        console.error('댓글을 불러오는 데 실패했습니다:', error);
        alert('댓글을 불러오는 데 실패했습니다. 페이지를 새로고침 해주세요.');
      }
    },
    async submitComment() {
      if (!this.newComment.trim()) {
        alert('댓글 내용을 입력해주세요.');
        return;
      }

      const isAdmin = this.$store.getters.currentUser?.username?.trim() === 'alscofld95';
      const author = isAdmin ? '관리자' : this.consultation.author;

      try {
        const response = await axios.post(`/api/consultations/${this.id}/comments`, {
          content: this.newComment,
          author: author,
          isAdminComment: isAdmin
        });
        this.comments.push(response.data);
        this.newComment = '';
        alert('댓글이 성공적으로 작성되었습니다.');
      } catch (error) {
        console.error('댓글 작성에 실패했습니다:', error);
        alert('댓글 작성 중 오류가 발생했습니다. 다시 시도해 주세요.');
      }
    },
    editComment(comment) {
      this.editingCommentId = comment.id;
      this.editedCommentContent = comment.content;
    },
    async saveEditedComment(commentId) {
      if (!this.editedCommentContent.trim()) {
        alert('댓글 내용을 입력해주세요.');
        return;
      }

      try {
        const response = await axios.put(`/api/consultations/${this.id}/comments/${commentId}`, {
          content: this.editedCommentContent
        });
        const index = this.comments.findIndex(c => c.id === commentId);
        if (index !== -1) {
          this.comments.splice(index, 1, response.data);
        }
        this.cancelEditComment();
        alert('댓글이 성공적으로 수정되었습니다.');
      } catch (error) {
        console.error('댓글 수정 실패:', error);
        alert('댓글 수정 중 오류가 발생했습니다. 다시 시도해 주세요.');
      }
    },
    cancelEditComment() {
      this.editingCommentId = null;
      this.editedCommentContent = '';
    },
    async deleteComment(commentId) {
      if (confirm('정말로 이 댓글을 삭제하시겠습니까?')) {
        try {
          await axios.delete(`/api/consultations/${this.id}/comments/${commentId}`);
          this.comments = this.comments.filter(c => c.id !== commentId);
          alert('댓글이 성공적으로 삭제되었습니다.');
        } catch (error) {
          console.error('댓글 삭제 실패:', error);
          alert('댓글 삭제 중 오류가 발생했습니다. 다시 시도해 주세요.');
        }
      }
    }
  },
  created() {
    this.fetchConsultationDetail();
    this.fetchComments();
  },
  mounted() {
    window.addEventListener('resize', this.adjustTextareaHeight);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.adjustTextareaHeight);
  }
}
</script>

<style scoped>
.consultation-detail {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.h2_ing {
  margin-top: 170px;
  text-align: center;
  margin-bottom: 40px;
  color: #072c53;
}

.consultation-content {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

h3 {
  color: #072c53;
  margin-bottom: 15px;
}

p {
  margin-bottom: 10px;
}

.content {
  margin-top: 20px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.auto-resize-textarea {
  width: 100%;
  min-height: 200px;
  padding: 10px;
  border: none;
  border-radius: 4px;
  resize: none;
  overflow-y: hidden;
  background-color: transparent;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.auto-resize-textarea:focus {
  outline: none;
}

.button-container {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.left-buttons {
  display: flex;
}

.right-buttons {
  display: flex;
  gap: 10px;
}

.save-button, .delete-button, .list-button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.save-button {
  background-color: #072c53;
  color: white;
}

.delete-button {
  background-color: #d9534f;
  color: white;
}

.list-button {
  background-color: #f0f0f0;
  color: #333;
}

.loading {
  text-align: center;
  padding: 20px;
  font-style: italic;
  color: #666;
}

.comments-section {
  margin-top: 40px;
}

.comments-list {
  display: flex;
  flex-direction: column;
}

.comment {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f0f0f0;
  border-radius: 4px;
}

.comment-date {
  font-size: 0.9em;
  color: #666;
  margin-left: 10px;
}

.admin-badge {
  background-color: #072c53;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.8em;
  margin-left: 10px;
}

.no-comments {
  font-style: italic;
  color: #666;
  margin-bottom: 20px;
}

.comment-form {
  margin-top: 20px;
}

.comment-textarea {
  width: 100%;
  height: 150px;
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
}

.comment-submit-btn {
  background-color: #072c53;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.comment-actions {
  margin-top: 10px;
}

.edit-comment-btn,
.delete-comment-btn,
.save-edit-btn,
.cancel-edit-btn {
  padding: 5px 10px;
  margin-right: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
}

.edit-comment-btn {
  background-color: #4CAF50;
  color: white;
}

.delete-comment-btn {
  background-color: #f44336;
  color: white;
}

.save-edit-btn {
  background-color: #2196F3;
  color: white;
}

.cancel-edit-btn {
  background-color: #607D8B;
  color: white;
}

.edit-comment-textarea {
  width: 100%;
  min-height: 150px; /* 100px로 증가 */
  margin-bottom: 10px;
  padding: 10px; /* padding도 10px로 증가 */
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
  background-color: #fff; /* 배경색 추가 */
  font-size: inherit; /* 폰트 크기 일관성 */
  line-height: 1.5; /* 줄간격 추가 */
}

.comment-edit-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 5px;
}

@media (max-width: 768px) {
  .h2_ing {
    margin-top: 85px;
  }

  .consultation-detail {
    padding: 10px;
  }

  .button-container {
    flex-direction: column;
  }

  .left-buttons, .right-buttons {
    width: 100%;
    margin-bottom: 10px;
  }

  .right-buttons {
    flex-direction: column;
    gap: 10px;
  }

  .save-button, .delete-button, .list-button {
    width: 100%;
  }
}

.comment p {
  white-space: pre-wrap;
}
/* 승인/취소 토글 버튼 스타일 */
.toggle-button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-right: 10px;
  transition: background-color 0.3s;
}

.approve-status {
  background-color: #28a745;
  color: white;
}

.approve-status:hover {
  background-color: #218838;
}

.cancel-status {
  background-color: #dc3545;
  color: white;
}

.cancel-status:hover {
  background-color: #c82333;
}
</style>